import { useState } from 'react';
import { usePageTitle } from '@/hooks/use-page-title';
import type { ActivityInterval, ActivityScope, UserActivityDataPoint } from '@/lib/api/user-activity';
import { IntervalSelector } from '@/components/dashboard/interval-selector';
import { ScopeSelector } from '@/components/dashboard/scope-selector';
import { ActivityChart } from '@/components/dashboard/activity-chart';
import { ActivityTable } from '@/components/dashboard/activity-table';
import { ExportMenu } from '@/components/dashboard/export-menu';
import { MetricDetailSheet } from '@/components/dashboard/metric-detail-sheet';
import { useUserActivity } from '@/hooks/queries/use-user-activity';
import { useAuthStore } from '@/stores/auth-store';
import { getInitials } from '@/lib/get-initials';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { WhatsNewCard } from '@/components/dashboard/whats-new-card';
import { DashboardSkeleton } from '@/components/dashboard/dashboard-skeleton';
import { ElapsedTimeNote } from '@/components/common/elapsed-time-note';
import { ThreeClocksPanel } from '@/components/common/three-clocks-panel';
import { Button } from '@/components/ui/button';
import { BarChart3 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

export type MetricType = 'features' | 'tasks' | 'decisions' | 'sessions' | 'epicRequests' | 'epics' | 'implementationTime';

const LIMITS_BY_INTERVAL: Record<ActivityInterval, number> = { day: 14, week: 10, month: 6 };

export function computeDateRange(
  data: UserActivityDataPoint[]
): { startDate: string; endDate: string } | null {
  if (data.length === 0) return null;
  const dates = data.flatMap((d) => [
    new Date(d.intervalStart).getTime(),
    new Date(d.intervalEnd).getTime(),
  ]);
  return {
    startDate: new Date(Math.min(...dates)).toISOString(),
    endDate: new Date(Math.max(...dates)).toISOString(),
  };
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** Mini sparkline SVG from data points */
function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) return null;
  const max = Math.max(1, ...data);
  const w = 80;
  const h = 24;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (v / max) * (h - 2) - 1;
    return `${String(x)},${String(y)}`;
  }).join(' ');

  return (
    <svg width={w} height={h} className="shrink-0 opacity-60">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function DashboardPage() {
  usePageTitle('Dashboard');
  const isMobile = useIsMobile();
  const [interval, setInterval] = useState<ActivityInterval>('week');
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<ActivityScope>('self');
  const [scopeId, setScopeId] = useState<string | undefined>(undefined);
  const [selectedMetric, setSelectedMetric] = useState<MetricType | null>(null);

  const user = useAuthStore((s) => s.user);
  const isGlobalAdmin = user?.isGlobalAdmin ?? false;
  const firstName = user?.name?.split(' ')[0] ?? 'there';

  const { data, isLoading, error, refetch } = useUserActivity(interval, page, LIMITS_BY_INTERVAL[interval], scope, scopeId);

  const activityData = data?.data ?? [];

  const handleIntervalChange = (v: ActivityInterval) => { setInterval(v); setPage(1); };
  const handleScopeChange = (s: ActivityScope, id?: string) => { setScope(s); setScopeId(id); setPage(1); };
  const handleMetricClick = (m: MetricType, c: number) => { if (c > 0) setSelectedMetric(m); };

  // Compute sparkline data
  const sorted = activityData.length > 0 ? [...activityData].reverse() : [];

  // Compute per-period values for growth calculation
  const lastPeriod = sorted.length > 0 ? sorted[sorted.length - 1] : null;
  const prevPeriod = sorted.length > 1 ? sorted[sorted.length - 2] : null;

  function growthPct(current: number, previous: number): number {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  }

  function formatDuration(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return h > 0 ? `${String(h)}h ${String(m)}m` : `${String(m)}m`;
  }

  const tiles: { label: string; value: number; metric: MetricType; dot: string; sparkColor: string; sparkData: number[]; growth: number; isTime?: boolean }[] = [
    { label: 'ER Created', value: lastPeriod?.epicRequestsCreated ?? 0, metric: 'epicRequests', dot: 'bg-cyan-400', sparkColor: '#22D3EE', sparkData: sorted.map(d => d.epicRequestsCreated), growth: lastPeriod && prevPeriod ? growthPct(lastPeriod.epicRequestsCreated, prevPeriod.epicRequestsCreated) : 0 },
    { label: 'Epics Created', value: lastPeriod?.epicsCreated ?? 0, metric: 'epics', dot: 'bg-indigo-400', sparkColor: '#818CF8', sparkData: sorted.map(d => d.epicsCreated), growth: lastPeriod && prevPeriod ? growthPct(lastPeriod.epicsCreated, prevPeriod.epicsCreated) : 0 },
    { label: 'Features Created', value: lastPeriod?.featuresCreated ?? 0, metric: 'features', dot: 'bg-blue-400', sparkColor: '#60A5FA', sparkData: sorted.map(d => d.featuresCreated), growth: lastPeriod && prevPeriod ? growthPct(lastPeriod.featuresCreated, prevPeriod.featuresCreated) : 0 },
    { label: 'Tasks Completed', value: lastPeriod?.tasksCompleted ?? 0, metric: 'tasks', dot: 'bg-emerald-400', sparkColor: '#34D399', sparkData: sorted.map(d => d.tasksCompleted), growth: lastPeriod && prevPeriod ? growthPct(lastPeriod.tasksCompleted, prevPeriod.tasksCompleted) : 0 },
    { label: 'Decisions Logged', value: lastPeriod?.decisionsLogged ?? 0, metric: 'decisions', dot: 'bg-amber-400', sparkColor: '#FBBF24', sparkData: sorted.map(d => d.decisionsLogged), growth: lastPeriod && prevPeriod ? growthPct(lastPeriod.decisionsLogged, prevPeriod.decisionsLogged) : 0 },
    { label: 'AI Sessions', value: lastPeriod?.aiSessions ?? 0, metric: 'sessions', dot: 'bg-purple-400', sparkColor: '#C084FC', sparkData: sorted.map(d => d.aiSessions), growth: lastPeriod && prevPeriod ? growthPct(lastPeriod.aiSessions, prevPeriod.aiSessions) : 0 },
    { label: 'Elapsed', value: lastPeriod?.totalElapsedMinutes ?? 0, metric: 'implementationTime', dot: 'bg-rose-400', sparkColor: '#FB7185', sparkData: sorted.map(d => d.totalElapsedMinutes), growth: lastPeriod && prevPeriod ? growthPct(lastPeriod.totalElapsedMinutes, prevPeriod.totalElapsedMinutes) : 0, isTime: true },
  ];

  if (error && !data) {
    return (
      <div className="glass-card rounded-2xl p-8 text-center md:p-12">
        <BarChart3 className="mx-auto mb-4 h-14 w-14 text-muted-foreground/20" />
        <p className="text-sm font-medium text-foreground">Unable to load dashboard activity</p>
        <p className="mx-auto mt-1.5 max-w-[280px] text-xs leading-relaxed text-muted-foreground">
          We couldn&apos;t load your latest dashboard data. Please try again.
        </p>
        <Button
          className="mt-5"
          onClick={() => {
            void refetch();
          }}
          size="sm"
          variant="outline"
        >
          Try again
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return <DashboardSkeleton isMobile={isMobile} showScopeSelector={isGlobalAdmin} />;
  }

  return (
    <div className="flex flex-col md:flex-row pt-4 gap-4 md:gap-0">
      {/* LEFT — Greeting, chart, stat tiles */}
      <div className="flex-1 pr-0 md:pr-6 space-y-3 md:space-y-5 min-w-0 overflow-hidden">
        {/* Greeting */}
        <div className={isMobile ? "space-y-3" : "flex items-center gap-3 justify-between"}>
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
              <AvatarFallback className="text-sm bg-primary/10 text-primary font-semibold">
                {getInitials(user?.name ?? firstName)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-xl font-semibold leading-tight">{getGreeting()}, {firstName}</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                {new Date().toLocaleDateString(undefined, isMobile ? { weekday: 'short', month: 'short', day: 'numeric' } : { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <ExportMenu data={activityData} interval={interval} />
            {isGlobalAdmin && (
              <ScopeSelector
                scope={scope}
                {...(scopeId !== undefined && { scopeId })}
                onScopeChange={handleScopeChange}
              />
            )}
            <div data-help-id="dashboard.intervalSelector">
              <IntervalSelector value={interval} onChange={handleIntervalChange} />
            </div>
          </div>
        </div>

        {activityData.length === 0 && (
          <div className="glass-card rounded-2xl p-8 md:p-12 text-center">
            <BarChart3 className="h-14 w-14 text-muted-foreground/20 mx-auto mb-4" />
            <p className="text-sm font-medium text-foreground">No activity yet</p>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-[240px] mx-auto leading-relaxed">Activity will appear here as you work on epics</p>
          </div>
        )}

        {activityData.length > 0 && (
          <>
            {/* Chart card */}
            <div className="glass-card rounded-xl p-5" data-help-id="dashboard.activityChart">
              <h2 className="text-sm font-medium mb-4">Activity</h2>
              <ActivityChart data={activityData} interval={interval} />
            </div>

            {/* What "Elapsed" means — always visible, never hover-only */}
            <ElapsedTimeNote />

            {/* Elapsed, active and compute as three separate values in the same
                view, each labelled with the question it answers. Never summed,
                and active time is not a corrected elapsed time. */}
            <ThreeClocksPanel
              className="glass-card"
              elapsedMinutes={lastPeriod ? lastPeriod.totalElapsedMinutes : null}
            />

            {/* Metric cards — responsive grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {tiles.map((tile) => {
                const isZero = tile.value === 0;
                const isPositive = tile.growth > 0;
                const isNegative = tile.growth < 0;
                return (
                  <div
                    key={tile.metric}
                    data-help-id={`dashboard.metrics.${tile.metric}`}
                    className={`glass-card rounded-xl ${isMobile ? 'p-3.5' : 'p-5'} transition-all ${
                      isZero ? 'opacity-40' : 'cursor-pointer hover:bg-white/[0.03] active:scale-[0.98]'
                    }`}
                    onClick={() => {
                      handleMetricClick(tile.metric, tile.value);
                    }}
                    role="button"
                    tabIndex={isZero ? -1 : 0}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>{tile.label}</span>
                      {!isMobile && <Sparkline data={tile.sparkData} color={tile.sparkColor} />}
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold tabular-nums`}>{tile.isTime ? formatDuration(tile.value) : tile.value}</span>
                      {tile.growth !== 0 && (
                        <span className={`text-xs font-medium ${isPositive ? 'text-emerald-400' : isNegative ? 'text-red-400' : 'text-muted-foreground'}`}>
                          {isPositive ? '↑' : '↓'} {Math.abs(tile.growth)}%
                        </span>
                      )}
                    </div>
                    {!isMobile && <p className="text-xs text-muted-foreground mt-1">compared to last {interval}</p>}
                  </div>
                );
              })}
            </div>

          </>
        )}
      </div>

      {/* RIGHT — What's New + Activity breakdown (below on mobile) */}
      <div className={`shrink-0 ${isMobile ? 'w-full border-t border-white/[0.06] pt-4' : 'w-[360px] border-l border-white/[0.06] pl-6'} space-y-4`}>
        {/* What's New card */}
        <div data-help-id="dashboard.whatsNew">
          <WhatsNewCard />
        </div>

        <div className="glass-card rounded-xl p-5" data-help-id="dashboard.activityBreakdown">
          <h2 className="text-sm font-medium mb-1">Activity Breakdown</h2>
          {/* Column headers — hidden on mobile where card layout is used */}
          <div className="hidden md:flex items-center px-3 pt-3 pb-2 text-xs text-muted-foreground uppercase tracking-wider">
            <span className="flex-1">Period</span>
            <span className="w-12 text-right">ER</span>
            <span className="w-12 text-right">Epics</span>
            <span className="w-12 text-right">Elapsed</span>
          </div>
          <ActivityTable data={activityData} interval={interval} />
        </div>
      </div>

      {/* Metric Detail Sheet */}
      {selectedMetric && (
        <MetricDetailSheet
          metricType={selectedMetric}
          open
          onOpenChange={(open) => {
            if (!open) {
              setSelectedMetric(null);
            }
          }}
          interval={interval}
          page={page}
          scope={scope}
          {...(scopeId !== undefined && { scopeId })}
          timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
          {...(lastPeriod?.intervalStart !== undefined && {
            startDate: lastPeriod.intervalStart,
          })}
          {...(lastPeriod?.intervalEnd !== undefined && {
            endDate: lastPeriod.intervalEnd,
          })}
        />
      )}
    </div>
  );
}
